import {
  StyleSheet, Text, View, useWindowDimensions,
} from 'react-native';
import React from 'react';
import Style from '../../constants/Style';
import Colors from '../../constants/Colors';

const TargetBar = ({
  current, target, unit = null, compare = false, dynamicSize = false,
}) => {
  const { width, height } = useWindowDimensions();
  const isSplitScreen1080p = width === 960 && height === 1080;

  // Dynamic font size functions
  const getWidgetTitleFontSize = () => {
    if (!dynamicSize) return 14;
    if (isSplitScreen1080p) return 12;
    if (height <= 540) return 10;
    if (height <= 720) return 12;
    if (height <= 1080) return 14;
    if (height <= 1440) return 18;
    return 24; // For height > 1440 (up to 2160)
  };

  const getWidgetValueFontSize = () => {
    if (!dynamicSize) return 30;
    if (isSplitScreen1080p) return 20;
    if (height <= 540) return 14;
    if (height <= 720) return 22;
    if (height <= 1080) return 30;
    if (height <= 1440) return 42;
    return 64; // For height > 1440 (up to 2160)
  };

  const getTargetFontSize = () => {
    if (!dynamicSize) return 16;
    if (isSplitScreen1080p) return 14;
    if (height <= 540) return 12;
    if (height <= 720) return 14;
    if (height <= 1080) return 16;
    if (height <= 1440) return 24;
    return 36; // For height > 1440 (up to 2160)
  };

  const getUnitTextFontSize = () => {
    if (!dynamicSize) return 14;
    if (isSplitScreen1080p) return 12;
    if (height <= 540) return 10;
    if (height <= 720) return 12;
    if (height <= 1080) return 14;
    if (height <= 1440) return 18;
    return 24; // For height > 1440 (up to 2160)
  };

  const getBarHeight = () => {
    if (!dynamicSize) return 16;
    if (isSplitScreen1080p) return 8;
    if (height <= 540) return 8;
    if (height <= 720) return 10;
    if (height <= 1080) return 16;
    if (height <= 1440) return 20;
    return 24; // For height > 1440 (up to 2160)
  };

  let fill = 0;
  let yellowFill = 0;
  let yellowFillPercent = 0;
  let excess = 0;
  let excessPercent = 0;
  if (current < target) {
    fill = (current / target) * 100;
    if (current > (target * 0.85)) {
      const last15 = target * 0.15;
      fill = 100;
      yellowFill = current - (target * 0.85);
      yellowFillPercent = (yellowFill / last15) * 100;
    }
  } else {
    fill = 100;
    excess = current - target;
    yellowFillPercent = 100;
    excessPercent = (excess / target) * 100;
    yellowFill = 100;
    if (excessPercent > 1 && excessPercent < 10) excessPercent = 15;
  }

  const getYellowBarColor = () => {
    if (excessPercent > 0) return Colors.light.hereworksRed500;
    return yellowFillPercent < 100 ? Colors.light.hereworksYellow500
      : Colors.light.hereworksRed500;
  };

  const getBlueBarColor = () => {
    if (fill < 100) return Colors.light.hereworksGreen500;
    if (excessPercent > 0) return Colors.light.hereworksRed500;
    if (yellowFillPercent > 0) return Colors.light.hereworksYellow500;
    return compare ? Colors.light.hereworksYellow500
      : Colors.light.hereworksBlue500;
  };

  return <>
  <View style={[Style.row, { justifyContent: 'flex-start' }]}>
  <View style={{ marginTop: isSplitScreen1080p ? 0 : 8 }}>
    <Text style={[styles.widgetTitle, { fontSize: getWidgetTitleFontSize() }]}>Current</Text>
    <View style={{ flexDirection: 'row', alignItems: 'flex-end' }}>
      <Text style={[styles.widgetValue, { fontSize: getWidgetValueFontSize() }]}>{current}</Text>
      <Text style={[styles.unitText, { fontSize: getUnitTextFontSize() }]}>{unit}</Text>
    </View>
  </View>
  {excessPercent > 0 ? <View style={{ marginLeft: 24, marginTop: 8 }}>
    <Text style={[styles.widgetTitle, { fontSize: getWidgetTitleFontSize() }]}>Exceed</Text>
    <View style={{ flexDirection: 'row', alignItems: 'flex-end' }}>
      <Text style={[
        styles.widgetValue,
        {
          fontSize: getWidgetValueFontSize(),
          color: Colors.light.hereworksRed500,
        },
      ]}>{excess}</Text>
      <Text style={[styles.unitText, { fontSize: getUnitTextFontSize() }]}>{unit}</Text>
    </View>
  </View> : null}
  </View>
  <View style={[Style.row, { alignItems: 'flex-start', paddingBottom: 20 }]}>
    <View style={{ flex: 4 }}>
      <View style={{
        height: getBarHeight(),
        backgroundColor: compare ? Colors.light.hereworksYellow50
          : Colors.light.hereworksGreen50,
        borderRadius: 100,
      }}>
        <View style={{
          width: `${Math.min(100, fill)}%`,
          flex: 1,
          backgroundColor: getBlueBarColor(),
          borderRadius: 100,
        }} />
      </View>
      <View style={[Style.row, { marginTop: 3 }]}>
        {target ? <Text style={[styles.widgetTitle,
          { fontSize: getWidgetTitleFontSize() }]}>Target</Text> : null}
      </View>
    </View>
    <View style={{ flex: 1 }}>
      <View style={{
        height: getBarHeight(),
        marginLeft: 8,
        backgroundColor: Colors.light.hereworksYellow50,
        borderRadius: 100,
      }}>
        <View style={{
          width: `${Math.min(100, Math.max(excessPercent, yellowFillPercent))}%`,
          flex: 1,
          backgroundColor: getYellowBarColor(),
          borderRadius: 100,
        }} />
      </View>
      <View style={{
        flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', marginTop: 3,
      }}>
        <Text style={[styles.target, { fontSize: getTargetFontSize() }]}>{target}</Text>
      </View>
    </View>
    <View style={{ flex: 1 }}>
      <View style={{
        height: getBarHeight(),
        marginLeft: 8,
        backgroundColor: Colors.light.hereworksRed50,
        borderRadius: 100,
      }}>
        <View style={{
          width: `${Math.min(100, excessPercent)}%`,
          flex: 1,
          backgroundColor: Colors.light.hereworksRed500,
          borderRadius: 100,
        }} />
      </View>
    </View>
  </View>
</>;
};

const styles = StyleSheet.create({
  widgetTitle: {
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  widgetValue: {
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    marginVertical: 4,
  },
  target: {
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
  },
  unitText: {
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    marginBottom: 14,
    marginLeft: 4,
  },
});

export default TargetBar;
